import { Injectable } from '@angular/core';
import { Observable, of, throwError } from 'rxjs';
import { LoggerService } from '../../../services/logger/logger.service';
import { InactiveTimerService } from '../../../services/inactive-timer.service';
import { ShopSettingsService } from './shop-settings.service';
import { PartnerStateService } from '../../partner/services/partner-state.service';
import { PartnerCodeStateService } from '../../partner-code/services/partner-code-state.service';
import { catchError, switchMap, tap } from 'rxjs/operators';

@Injectable()
export class BaseStepService<Step> {
  protected _step: Step;

  protected _steps: Step[] = [];

  protected _stepIndex = -1;

  public authorizationAsGuest: boolean;

  constructor(
    protected logger: LoggerService,
    protected inactiveTimerService: InactiveTimerService,
    protected shopSettingsService: ShopSettingsService,
    protected partnerStateService: PartnerStateService,
    protected partnerCodeStateService: PartnerCodeStateService,
  ) { }

  protected get serviceName(): string {
    return BaseStepService.name;
  }

  get step(): Step {
    return this._step;
  }

  protected get initStep(): Step {
    return null;
  }

  protected get resetStep(): Step {
    return null;
  }

  reinitialize(): Observable<boolean> {
    return of(true).pipe(
      tap(_ => {
        this.logger.info(`${this.serviceName}: reinitialize.`);
        this._clearCache();
      }),
      switchMap(_ => this.initialize()),
      tap(_ => this.logger.info(`${this.serviceName}: reinitialize - initialize done.`)),
      catchError(error => {
        this.logger.info(`${this.serviceName}: reinitialize - initialize error ${error}.`);
        return throwError(error);
      }),
    );
  }

  protected _clearCache(): void {
  }

  protected _fillCache(): Observable<boolean> {
    return of(true);
  }

  protected _internalInitialize(): void {
    this._step = this.resetStep;
    this._stepIndex = -1;
  }

  initialize(): Observable<boolean> {
    return of(true).pipe(
      tap(_ => {
        this.logger.info(`${this.serviceName}: initialize.`);
        this._step = this.initStep;
        this.authorizationAsGuest = false;
      }),
      switchMap(_ => this._fillCache()),
      tap(_ => {
        this._internalInitialize();
      })
    );
  }

  isAvailableStep(step: Step): boolean {
    if (!step) {
      return false;
    }

    return true;
  }

  nextStep(): boolean {
    const steps = this._steps;
    this._stepIndex++;

    while (steps.length > this._stepIndex) {
      const nextStep = steps[this._stepIndex];

      if (this.isAvailableStep(nextStep)) {
        this.update(nextStep);
        return true;
      }
      this._stepIndex++;
    }

    this._stepIndex = -1;
    return false;
  }

  prevStep(): boolean {
    const steps = this._steps;
    if (steps.length <= this._stepIndex) {
      this._stepIndex = steps.length - 1;
    }
    this._stepIndex--;
    while (this._stepIndex >= 0) {
      const prevStep = steps[this._stepIndex];
      if (prevStep === this.prevStepForIndexMinusOne) {
        this._stepIndex = -1;
        return false;
      }

      if (this.isAvailableStep(prevStep)) {
        this.update(prevStep);
        return true;
      }
      this._stepIndex--;
    }
    this._stepIndex = -1;
    return false;
  }

  protected get prevStepForIndexMinusOne(): Step {
    return null;
  }

  update(
    step: Step = this.resetStep,
  ): void {

    this._step = step;
    this.inactiveTimerService.resetTimer();

    // switch (this._step) {
    //   default:
    //     this.initialize().subscribe();
    //     break;
    // }
  }

  back(): void {
    if (this.prevStep()) {
      return;
    }

    this.update(this.initStep);
  }

  forward(): void {
    if (this.nextStep()) {
      return;
    }

    this.update(this.initStep);
  }

  protected get needAuth(): boolean {
    return (!this.shopSettingsService.isVUMode || this.partnerCodeStateService.enabled)
      && !this.authorizationAsGuest
      && this.partnerStateService.loggedInPartner == null;
  }
}
